/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../globalScope/GlobalScope';
import IDisposable from '../lang/IDisposable';
import Disposable from '../lang/Disposable';
import DisposableList from '../lang/DisposableList';
import Subject from '../rx/Subject';

export default class NetworkMonitor implements IDisposable {
  private readonly _disposables: DisposableList = new DisposableList();
  private readonly _isSupported: boolean = GlobalScope.value.navigatorConnection !== undefined;
  private readonly _rtt: Subject<number | undefined> = new Subject<number | undefined>(undefined);
  private readonly _effectiveType: Subject<string | undefined> = new Subject<string | undefined>(undefined);
  private readonly _downlinkThroughputCapacity: Subject<number | undefined> = new Subject<number | undefined>(undefined);

  constructor() {
    if (!this.isSupported) {
      return;
    }

    this.setupNetworkChangeListeners();
    this.updateStatistics();
  }

  get isSupported(): boolean {
    return this._isSupported;
  }

  get rtt(): Subject<number | undefined> {
    return this._rtt;
  }

  get effectiveType(): Subject<string | undefined> {
    return this._effectiveType;
  }

  get downlinkThroughputCapacity(): Subject<number | undefined> {
    return this._downlinkThroughputCapacity;
  }

  dispose(): void {
    this._disposables.dispose();
  }

  private getDownlinkThroughputCapacity(): number {
    return GlobalScope.value.navigatorConnection?.downlink ?? GlobalScope.value.navigatorConnection?.downlinkMax ?? -1;
  }

  private getEffectiveType(): string {
    return GlobalScope.value.navigatorConnection?.effectiveType ?? GlobalScope.value.navigatorConnection?.type ?? 'Unknown';
  }

  private getRoundTripTime(): number {
    return GlobalScope.value.navigatorConnection?.rtt ?? -1;
  }

  private setupNetworkChangeListeners(): void {
    const listener = (): void => this.updateStatistics();

    GlobalScope.value.navigatorConnection?.addEventListener('change', listener);

    this._disposables.add(new Disposable(() => GlobalScope.value.navigatorConnection?.removeEventListener('change', listener)));
  }

  private updateStatistics(): void {
    this._rtt.value = this.getRoundTripTime();
    this._effectiveType.value = this.getEffectiveType();
    this._downlinkThroughputCapacity.value = this.getDownlinkThroughputCapacity();
  }
}